/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: Calendar.tsx
 * Author: Austin Robinson
 * Date Created: 1/29/2022
 * Copyright (c) DrivingDoc 2022
 */
import { useEffect, useMemo, useRef, useState } from 'react';

// import { useSoftUIController } from '@/context';
import { Interval } from '@flatten-js/interval-tree';
import { DateSelectArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
// import adaptivePlugin from '@fullcalendar/adaptive';
import listPlugin from '@fullcalendar/list';
import momentPlugin from '@fullcalendar/moment';
// import '@fullcalendar/react/dist/vdom';

// import Calendar from '@/examples/Calendar';
import FullCalendar from '@fullcalendar/react';
import resourceTimegridPlugin from '@fullcalendar/resource-timegrid';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

import { CalendarCoursePeriod } from 'api/hooks/private/periods/GetAllCoursePeriods.ts';
import { Instructor } from 'api/hooks/private/users/instructors/GetInstructors.ts';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog/FeedbackDialog.tsx';
import DriveOverviewDialog from 'components/Dialogs/Periods/DriveOverviewDialog';
import PageContainer from 'components/PageContainer/PageContainer.tsx';

import useAuthContext from 'hooks/useAuthContext.ts';
import useSearchParamState from 'hooks/useSearchParamState.ts';

import { useSidebarContext } from 'layouts/DashboardLayout/Sidebar/index.ts';

import { getApiErrorMessage } from 'utils/getApiErrorMessage.ts';

import { default as DriveContainer } from './components/scheduling/components/Drive';
import useScheduler from './components/scheduling/components/useScheduler';
import { SCHEDULING_PANEL_WIDTH } from './components/scheduling/components/useScheduler/SchedulingPanel.tsx';
// import useDriveOverviewDialog from './components/scheduling/dialogs/useDriveOverviewDialog';
import useModifyCoursePeriodDialog from './components/scheduling/dialogs/useModifyCoursePeriodDialog';
import usePeriodEnrollmentDialog from './components/scheduling/dialogs/usePeriodEnrollmentDialog';
import headerFix from './components/scheduling/headerFix.ts';
import usePeriodContextMenu from './components/usePeriodContextMenu';
import './styles.css';

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme }) => ({
  flexGrow: 1,
  // padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: 0,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: `${SCHEDULING_PANEL_WIDTH}px`,
      },
    },
  ],
}));

const defaultTimeWindow = {
  // start: "00:00:00", // max time
  // end: "24:00:00" // min time
  start: '06:00:00', // max time
  end: '24:00:00', // min time
};

function LegacyCalendar() {
  const auth = useAuthContext();
  const calendar = useRef<FullCalendar>(null); // store reference to calendar
  // const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [timeWindow] = useState(defaultTimeWindow);
  const { isNavigationExpanded } = useSidebarContext();
  // const [controller, dispatch] = useSoftUIController();
  const navigate = useNavigate();
  const [viewType, setViewType] = useSearchParamState({
    paramName: 'viewType',
    initialState: 'dayGridMonth',
    serialize: String,
    deserialize: String,
  });
  const [date, setDate] = useSearchParamState({
    paramName: 'date',
    initialState: dayjs().startOf('hour').toISOString(),
    serialize: String,
    deserialize: String,
  });

  // const [viewType, setViewType] = useState('dayGridMonth');
  // const [date, setDate] = useState(new Date().toISOString());
  // const { registerStudentsForDrive } = useModifyDrive();
  const { calendarData, ...schedulingPanel } = useScheduler({
    calendarRef: calendar,
  });
  // todo might have issue where drive was updated to be outside of current filter and wont show up without refresh
  const { openModifyDriveDialog, ModifyDriveDialog } = useModifyCoursePeriodDialog();
  // const { openDriveOverviewDialog, DriveOverviewDialog: OverviewDialog } = useDriveOverviewDialog();
  const { openPeriodEnrollmentDialog, PeriodEnrollmentDialog } = usePeriodEnrollmentDialog();
  const dialogs = useDialogs();
  const { PeriodContextMenu, openPeriodContextMenu } = usePeriodContextMenu({
    options: [
      {
        title: 'Modify',
        onClick: (coursePeriodId) => openModifyDriveDialog(coursePeriodId),
      },
      {
        title: 'Enroll Student',
        onClick: (coursePeriodId) => openPeriodEnrollmentDialog(coursePeriodId),
      },
      {
        title: 'Delete',
        onClick: async (coursePeriodId) => {
          try {
            // show confirmation dialog
            const confirmed = await dialogs.confirm('Any existing registrations will also be deleted.', {
              okText: 'Delete',
              cancelText: 'Cancel',
              severity: 'error',
            });

            // if user cancels, return
            if (!confirmed) {
              return;
            }

            // todo invoke delete method in scheduler
            await schedulingPanel.deleteDrive([coursePeriodId]);
            // const deletedDrive = await calendarData.deleteDrive.mutateAsync(driveID);

            // schedulingPanel.reRender();
            dialogs.open(FeedbackDialog, {
              title: 'Deleted',
              icon: 'success',
            });
          } catch (error) {
            dialogs.open(FeedbackDialog, {
              title: 'Error ',
              message: getApiErrorMessage(error),
            });
          }
        },
      },
    ],
  });

  // const { miniSidenav } = controller;

  const onClickEmptySeat = (calendarElement: any) => {
    const coursePeriod = calendarElement.event.toPlainObject().extendedProps.coursePeriod;
    openPeriodEnrollmentDialog(coursePeriod.id);
  };

  const onClickStudent = () => {};

  const onClickDrive = (period: CalendarCoursePeriod, studentId: string) => {
    if (period?.course_section?.course?.course_type === 'behind_the_wheel') {
      // openDriveOverviewDialog(period.id, studentId);
      dialogs.open(DriveOverviewDialog, {
        periodId: period.id,
        // periodId: 'period-multi-001',
        studentId, // Optional: auto-select this student's tab
      });
    } else {
      navigate(`/admin/periods/${period?.id}`);
    }
  };

  const renderEventContent = (eventInfo: any) => {
    // const { drive, isNewDrive } = eventInfo.event.extendedProps;
    const isNewDrive = eventInfo.event.extendedProps.isNewDrive as boolean;
    const coursePeriod = eventInfo.event.extendedProps.coursePeriod as CalendarCoursePeriod;
    // const firstInstructor = coursePeriod.course_period_instructor[0].instructor;

    if (isNewDrive) {
      return (
        <DriveContainer
          calendarEvent={eventInfo}
          coursePeriod={coursePeriod}
          editMode={editMode}
          onclickEmptySeat={onClickEmptySeat}
          onclickStudent={onClickStudent}
          onclickDrive={onClickDrive}
          hideFillSeat={false}
        />
      );
    }

    if (eventInfo.isMirror || !coursePeriod.course_period_instructors) {
      return (
        <div className="fc-daygrid-event fc-daygrid-dot-event fc-event fc-event-start fc-event-end fc-event-past">
          <div className="fc-event-time">{eventInfo.timeText}</div>
        </div>
      );
    }

    if (['dayGridMonth'].includes(eventInfo.view.type)) {
      return (
        <div className="fc-daygrid-event fc-daygrid-dot-event fc-event fc-event-start fc-event-end fc-event-past">
          <div
            className="fc-daygrid-event-dot"
            style={{ borderColor: coursePeriod.course_period_instructors?.[0]?.instructor.calendar_color ?? 'none' }}
          />
          <div className="fc-event-time">{eventInfo.timeText}</div>
          <div className="fc-event-title">
            {`${coursePeriod.course_period_instructors?.[0]?.instructor.user.nickname ?? coursePeriod.course_period_instructors?.[0]?.instructor.user.first_name} ${coursePeriod.course_period_instructors?.[0]?.instructor.user.last_name}`}
          </div>
          {/* <div className="fc-event-title">{`(${drive.registrations.items.length}/${drive.maxRegistrations})`}</div> */}
        </div>
      );
    }
    // if (coursePeriod.course_section.course.course_type === 'behind_the_wheel') {
    //   return (
    //     <DriveContainer
    //       calendarEvent={eventInfo}
    //       coursePeriod={coursePeriod}
    //       editMode={editMode}
    //       onclickEmptySeat={onClickEmptySeat}
    //       onclickStudent={onClickStudent}
    //       onclickDrive={onClickDrive}
    //       hideFillSeat={false}
    //     />
    //   );
    // }
    // return (
    //   // <div
    //   //   className="fc-daygrid-event fc-daygrid-dot-event fc-event fc-event-start fc-event-end fc-event-past"
    //   //   // onClick={() => {onClickDrive(coursePeriod)}
    //   // }
    //   // >
    //   //   <div className="fc-event-time">{eventInfo.timeText}</div>
    //   // </div>
    //     <Grid
    //         container
    //         direction="column"
    //     >
    //
    //       <Grid container direction="row" justifyContent="space-between">
    //         <Grid item pl={1}>
    //           <Typography variant="h6" component="div" sx={{ color: getFontColor(eventInfo.backgroundColor, 0.8), lineHeight: 'normal', textDecoration: 'underline' }}>{eventInfo.timeText}</Typography>
    //         </Grid>
    //         <Grid item px={1}>
    //           <Typography variant="h6" component="div" sx={{ color: getFontColor(eventInfo.backgroundColor, 0.8), lineHeight: 'normal', textDecoration: 'medium' }}>{coursePeriod?.course_section.title}</Typography>
    //         </Grid>
    //       </Grid>
    //
    //       <Grid container direction="row" justifyContent="space-between" pb={0.5}>
    //
    //         <Grid item pl={1}>
    //           {coursePeriod.course_period_instructors.map((course_period_instructor) => {
    //             return (<Typography variant="h6" component="div" sx={{ color: getFontColor(eventInfo.backgroundColor, 0.8), lineHeight: 'normal', fontWeight: 'medium' }}>{`${course_period_instructor.instructor.user.first_name} ${course_period_instructor.instructor.user.last_name}`}</Typography>)
    //           })}
    //         </Grid>
    //       </Grid>
    //     </Grid>
    // );
    return (
      <DriveContainer
        calendarEvent={eventInfo}
        coursePeriod={coursePeriod}
        editMode={editMode}
        onclickEmptySeat={onClickEmptySeat}
        onclickStudent={onClickStudent}
        onclickDrive={onClickDrive}
        hideFillSeat={false}
      />
    );
  };

  const renderResourceLabel = (arg: any) => {
    const events = schedulingPanel.scheduler.getVisibleResourceEvents(arg.resource.id);
    const numOfCoursePeriods = events.existingEvents.length + events.newEvents.length;
    return (
      <Box>
        <Typography variant="subtitle1" noWrap overflow="hidden" textOverflow="ellipsis">
          {arg.resource.title}
        </Typography>
        <Typography
          variant="subtitle2"
          noWrap
        >{`${numOfCoursePeriods} ${numOfCoursePeriods === 1 ? 'Course Period' : 'Course Periods'}`}</Typography>
      </Box>
    );
  };

  useEffect(() => {
    if (editMode) {
      try {
        schedulingPanel.open();
      } catch (e) {
        console.log('Something Happened');
        // captureException(e);
      }
    } else {
      schedulingPanel.close();
    }
    setTimeout(() => {
      (calendar as any).current.getApi().updateSize();
      headerFix();
    }, 300);
  }, [editMode]);

  useEffect(() => {
    schedulingPanel.reRender();
  }, [calendarData.coursePeriods]);

  useEffect(() => {
    window.addEventListener('resize', headerFix);

    return () => {
      window.removeEventListener('resize', headerFix);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      calendar.current?.getApi().updateSize();
      headerFix();
    }, 300);
  }, [isNavigationExpanded]);

  const cal = (
    <FullCalendar
      ref={calendar}
      schedulerLicenseKey="0419671975-fcs-1638902584"
      plugins={[
        momentPlugin,
        interactionPlugin,
        resourceTimegridPlugin,
        dayGridPlugin,
        listPlugin /*, adaptivePlugin*/,
      ]}
      stickyHeaderDates
      initialView={viewType}
      initialDate={date}
      height="100%"
      viewDidMount={(arg: any) => {
        // setTimeout is a temp fix for the state update conflict
        // setTimeout(() => {
        //   setViewType(arg.view.type);
        // }, 100);
        setViewType(arg.view.type);
        // const updatedInterval = new Interval(arg.view.activeStart.getTime(), arg.view.activeEnd.getTime());
        // scheduler.setCurrentInterval(updatedInterval);
        // updateSchedulingInterval(updatedInterval);
        if (arg.type !== 'dayGridMonth') {
          // setTimeout(() => {
          //   headerFix();
          // }, 500);
          headerFix();
        }
        // if (!calendar.current) {
        //   return;
        // }
        // if (arg.view.type === 'listWeek' || arg.view.type === 'listDay') {
        //   (calendar as any).current.getApi().setOption('eventOrder', 'start,title');
        // } else {
        //   (calendar as any).current.getApi().setOption('eventOrder', 'title,start');
        // }
      }}
      datesSet={(dateInfo: any) => {
        // console.log('Date Info');
        // console.log(dateInfo);
        setDate(dateInfo.view.activeStart.toISOString());
        const updatedInterval = new Interval(dateInfo.view.activeStart.getTime(), dateInfo.view.activeEnd.getTime());
        // scheduler.setCurrentInterval(updatedInterval);
        schedulingPanel.updateSchedulingInterval(updatedInterval);
        schedulingPanel.checkResourcesWithEvents(true);
        if (dateInfo.view.type === 'resourceTimeGridDay') {
          schedulingPanel.disableSelection(false);
          // schedulingPanel.checkResourcesWithEvents(true);
        } else {
          schedulingPanel.disableSelection(true);
          // schedulingPanel.uncheckAllResources();
        }
        // console.log('Date Changed, fixing header');
        headerFix();
      }}
      nowIndicator
      selectable={editMode}
      eventOverlap={false}
      customButtons={{
        addDrivesBtn: editMode
          ? {
              text: 'Done',
              click: () => {
                // schedulingPanel.checkResourcesWithEvents(true);
                setEditMode(false);
              },
            }
          : {
              text: 'Add Course Periods',
              click: () => {
                schedulingPanel.checkResourcesWithEvents(true);
                setEditMode(true);
              },
            },
      }}
      headerToolbar={{
        center: auth?.userRole === 'ADMIN' ? 'addDrivesBtn' : undefined,
        right: 'listWeek,listDay dayGridMonth,timeGridWeek,resourceTimeGridDay prev,next,today',
      }}
      views={{
        listDay: {
          type: 'list',
          buttonText: 'Day List',
          titleFormat: 'dddd MMMM D, YYYY',
          navLinkDayClick: 'resourceTimeGridDay',
        },
        listWeek: {
          type: 'list',
          buttonText: 'Week List',
          navLinkDayClick: 'listDay',
        },
        dayGridMonth: {
          type: 'dayGrid',
          dayMaxEventRows: 3,
          buttonText: 'Month',
          navLinkDayClick: 'resourceTimeGridDay',
        },
        timeGridWeek: {
          buttonText: 'Week',
          navLinkDayClick: 'resourceTimeGridDay',
        },
        resourceTimeGridDay: {
          type: 'timeGrid',
          buttonText: 'Day',
          titleFormat: 'dddd MMMM D, YYYY',
          // eventShortHeight: 60,
        },
      }}
      slotMinTime={timeWindow.start}
      slotMaxTime={timeWindow.end}
      slotDuration="00:15:00"
      filterResourcesWithEvents={!editMode}
      resources={(_fetchInfo: any, successCallback: any, _failureCallback: any) => {
        // console.log(fetchInfo);
        if (!calendarData.instructors) {
          successCallback([]);
        }
        if (editMode) {
          successCallback(
            schedulingPanel.getCheckedResources().map((instructor: Instructor) => ({
              id: instructor.user_id,
              instructor,
              title: `${instructor.user.nickname ?? instructor.user.first_name} ${instructor.user.last_name}`,
              eventColor: instructor.calendar_color,
            })),
          );
        }

        successCallback(
          calendarData.instructors?.map((instructor: Instructor) => ({
            id: instructor.user_id,
            instructor,
            title: `${instructor.user.nickname ?? instructor.user.first_name} ${instructor.user.last_name}`,
            eventColor: instructor.calendar_color,
          })) ?? [],
        );
      }}
      resourceOrder="title"
      allDaySlot={false}
      slotEventOverlap
      eventTimeFormat={{
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: true,
        meridiem: 'short',
      }}
      lazyFetching
      events={(info: any, successCallback: any) => {
        if (
          calendarData.calendarFilter === undefined ||
          info.startStr !== calendarData.calendarFilter?.start ||
          info.endStr !== calendarData.calendarFilter?.end
        ) {
          calendarData.setCalendarFilter({
            start: info.startStr,
            end: info.endStr,
          });
          // setTimeWindow(defaultTimeWindow);
        }

        if (calendarData.coursePeriods) {
          console.time('Parse Events');
          const events = calendarData.coursePeriods.map((coursePeriod: CalendarCoursePeriod) => ({
            id: coursePeriod.id,
            resourceId: coursePeriod.course_period_instructors?.[0]?.instructor_user_id,
            // to show drive for all instructors
            // resourceIds: coursePeriod.course_period_instructors.map(periodInstructor => periodInstructor?.instructor_user_id),
            title: `${coursePeriod.course_period_instructors?.[0]?.instructor.user.nickname ?? coursePeriod.course_period_instructors?.[0]?.instructor.user.first_name} ${coursePeriod.course_period_instructors?.[0]?.instructor.user.last_name}`,
            start: coursePeriod.start,
            end: coursePeriod.end,
            extendedProps: {
              coursePeriod,
            },
          }));
          // const flattened = events.flat();
          console.timeEnd('Parse Events');
          successCallback(events);
        }
      }}
      eventContent={renderEventContent}
      resourceLabelContent={renderResourceLabel}
      eventDidMount={(arg: any) => {
        if (arg.isMirror) {
          return;
        }

        const { coursePeriod } = arg.event.toPlainObject().extendedProps;

        // const startTime = new Date(coursePeriod.start).toLocaleTimeString([], { hour12: false });
        // const endTime = new Date(coursePeriod.end).toLocaleTimeString([], { hour12: false });
        //
        // if (startTime < timeWindow.start) {
        //   setTimeWindow((prevState) => ({
        //     start: startTime,
        //     end: prevState.end
        //   }));
        // }
        //
        // if (endTime > timeWindow.end) {
        //   setTimeWindow((prevState) => ({
        //     start: prevState.start,
        //     end: endTime
        //   }));
        // }

        if (!arg.event.extendedProps.isNewDrive) {
          schedulingPanel.scheduler.addExistingDrive(coursePeriod, arg.el);
          arg.el.addEventListener('contextmenu', (event: MouseEvent) => {
            if (auth?.userRole === 'ADMIN') {
              openPeriodContextMenu(coursePeriod.id, event, arg);
            }
          });
        }
      }}
      selectAllow={(selectInfo: any) => {
        console.log(selectInfo);
        if (selectInfo.resource) {
          return schedulingPanel.scheduler.eventAllowed(
            new Interval(selectInfo.start.getTime(), selectInfo.end.getTime()),
            selectInfo.resource.id,
            schedulingPanel.options.duration,
            4,
            schedulingPanel?.selectedCourseType !== 'behind_the_wheel',
          );
        }
        return false;
      }}
      select={(selectionInfo: DateSelectArg) => {
        if (selectionInfo.view.type === 'resourceTimeGridDay') {
          schedulingPanel.createDrives(selectionInfo);
          schedulingPanel.reRender();
        }
      }}
      eventOrder="start,title"
      navLinks
    />
  );

  const masterCalendar = useMemo(
    () => cal,
    [
      calendarData.instructors,
      calendarData.coursePeriods,
      editMode,
      schedulingPanel.getCheckedResources,
      schedulingPanel.panel,
      timeWindow,
    ],
  );

  return (
    <PageContainer
    // title="Calendar"
    // stickyHeader
    // slotProps={{
    //   header: {
    //     slots: {
    //       toolbar: () => <CalendarToolbar calendarApi={calendar} />,
    //     },
    //   },
    // }}
    >
      <Main open={editMode}>
        {masterCalendar}
        {ModifyDriveDialog}
        {/* {OverviewDialog} */}
        {PeriodEnrollmentDialog}
        {PeriodContextMenu}
      </Main>
      {schedulingPanel.panel}
    </PageContainer>
  );
}

export default LegacyCalendar;
